import AppGlobalsContext from "app/AppGlobalsContext";
import { COUPON_STATUS, COUPON_UPCOMING_FOR, RESOLUTION } from "app/constants";
import { Amount } from "app/components/ui/Amount/Amount";
import MyCouponInfo from "app/pages/Account/MyCoupons/MyCouponInfo/MyCouponInfo";
import { messagePropType, messagePropTypeRequired } from "app/utils/propTypes";
import classNames from "classnames";
import PropTypes from "prop-types";
import { memo, useContext, useLayoutEffect, useRef, useState } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { isTsCurrentDay } from "app/utils/utils";
import "./MyCouponCredit.scss";

const creditSponsorshipDescription = type =>
	({
		[COUPON_UPCOMING_FOR.friendBooked]: (
			<FormattedMessage id="mycoupons.credit.sponsorship.friendBooked.description" />
		),
		[COUPON_UPCOMING_FOR.friendSubscribed]: (
			<FormattedMessage id="mycoupons.credit.sponsorship.friendSubscribed.description" />
		),
		[COUPON_UPCOMING_FOR.sponsorshipAccepted]: (
			<FormattedMessage id="mycoupons.credit.sponsorship.sponsorshipAccepted.description" />
		),
	})[type] || "";

const MyCouponCredit = ({
	type,
	title,
	label,
	description,
	conditions,
	creationDate,
	status,
	amount = 0,
	balance = 0,
	usableAmount = 0,
	minUseDate,
	maxUseDate,
	cta,
	icon,
	uses = [],
	source,
	friendEmail,
	sourceBookingReference,
	infoExt,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const [isTitleLineClamped, updateTitleLineClamped] = useState(
		isMobile ? false : label?.length > 25 || title?.length > 25
	);
	const [isDescLineClamped, updateDescLineClamped] = useState(false);
	const titleRef = useRef();
	const descRef = useRef();

	useLayoutEffect(() => {
		if (title && titleRef.current) {
			updateTitleLineClamped(
				titleRef.current.offsetHeight < titleRef.current.scrollHeight === true
			);
		}
	}, [titleRef, title]);

	useLayoutEffect(() => {
		if (description && descRef.current) {
			updateDescLineClamped(descRef.current.offsetHeight < descRef.current.scrollHeight);
		}
	}, [descRef, description]);

	if (cta) {
		return (
			<div className="my-coupon-credit my-coupon-credit--cta" data-testid="title">
				<div className="my-coupon-credit__content">
					<div className="my-coupon-credit__type">
						<FormattedMessage id={`mycoupons.credit.type.${type?.toLowerCase()}`} />
					</div>
					<div className="my-coupon-credit__title">{title}</div>
					{cta}
				</div>
				<div className="my-coupon-credit__amount">{icon}</div>
			</div>
		);
	}

	const disabled = [COUPON_STATUS.EXPIRED, COUPON_STATUS.USED].includes(status);
	const shouldShowAmount = [
		COUPON_STATUS.USED,
		COUPON_STATUS.EXPIRED,
		COUPON_STATUS.UPCOMING,
	].includes(status);
	const isComingSoon = status === COUPON_STATUS.UPCOMING;

	const isCreditedFromFriendSponsor = [
		COUPON_UPCOMING_FOR.friendBooked,
		COUPON_UPCOMING_FOR.friendSubscribed,
		COUPON_UPCOMING_FOR.sponsorshipAccepted,
	].includes(source);

	const itemClass = classNames("my-coupon-credit", {
		"my-coupon-credit--disabled": disabled,
		"my-coupon-credit--upcoming": isComingSoon,
	});

	let statusNode;
	if (disabled) {
		let usedDate;
		if (status === COUPON_STATUS.USED) {
			const usesSorted = uses.sort((a, b) => (a.date > b.date ? -1 : 1));
			usedDate = usesSorted[0]?.date;
		} else {
			usedDate = maxUseDate;
		}
		statusNode = (
			<div className="my-coupon-credit__status my-coupon-credit__status--disabled">
				<FormattedMessage
					id={
						status === COUPON_STATUS.USED
							? "mycoupons.credit.use.label"
							: "mycoupons.credit.expiration.label"
					}
					values={{
						date: (
							<FormattedDate
								timeZone="UTC"
								month="long"
								day="2-digit"
								value={usedDate}
							/>
						),
					}}
				/>
			</div>
		);
	} else if (isComingSoon) {
		statusNode = (
			<div className="my-coupon-credit__status-list">
				{source === COUPON_UPCOMING_FOR.friendSubscribed && (
					<div className="my-coupon-credit__status my-coupon-credit__status--upcoming">
						<FormattedMessage id="mycoupons.credit.upcoming.friendSubscribed.label" />
					</div>
				)}
				{source === COUPON_UPCOMING_FOR.friendBooked && (
					<div className="my-coupon-credit__status my-coupon-credit__status--upcoming">
						<FormattedMessage id="mycoupons.credit.upcoming.friendBooked.label" />
					</div>
				)}
				{minUseDate && (
					<div className="my-coupon-credit__status my-coupon-credit__status--upcoming">
						<FormattedMessage
							id="mycoupons.credit.upcoming.date"
							values={{
								date: (
									<FormattedDate
										timeZone="UTC"
										month="long"
										day="2-digit"
										value={minUseDate}
									/>
								),
							}}
						/>
					</div>
				)}
			</div>
		);
	} else if (status === COUPON_STATUS.EXPIRE_SOON) {
		statusNode = (
			<div className="my-coupon-credit__status my-coupon-credit__status--expired-soon">
				<FormattedMessage
					id={
						isTsCurrentDay(maxUseDate)
							? "mycoupons.credit.expire.today.label"
							: "mycoupons.credit.expire.label"
					}
					values={{
						date: (
							<FormattedDate
								timeZone="UTC"
								month="long"
								day="2-digit"
								value={maxUseDate}
							/>
						),
					}}
				/>
			</div>
		);
	} else {
		statusNode = (
			<div className="my-coupon-credit__status my-coupon-credit__status--available">
				<FormattedMessage
					id="mycoupons.credit.expire.label"
					values={{
						date: (
							<FormattedDate
								timeZone="UTC"
								month="long"
								day="2-digit"
								value={maxUseDate}
							/>
						),
					}}
				/>
			</div>
		);
	}

	let couponTitle;
	if (label) {
		couponTitle = label;
	} else if (isCreditedFromFriendSponsor) {
		couponTitle = friendEmail;
	} else {
		couponTitle = title;
	}

	let amountValue;
	if (usableAmount !== 0) {
		amountValue = usableAmount;
	} else if (shouldShowAmount) {
		amountValue = amount;
	} else {
		amountValue = balance;
	}

	const couponDescription = isCreditedFromFriendSponsor
		? creditSponsorshipDescription(source)
		: description;
	return (
		<div className={itemClass} data-testid="my-coupon-credit">
			<div className="my-coupon-credit__content">
				<div className="my-coupon-credit__type">
					<FormattedMessage id={`mycoupons.credit.type.${type?.toLowerCase()}`} />
				</div>
				<div className="my-coupon-credit__title" ref={titleRef}>
					{couponTitle}
				</div>
				{couponDescription && (
					<div className="my-coupon-credit__description" ref={descRef}>
						{couponDescription}
					</div>
				)}
				{conditions && conditions}
				{statusNode}
			</div>
			<div className="my-coupon-credit__amount">
				<Amount value={amountValue} size="xl" className="my-coupon-credit__amount-value" />
			</div>
			{![COUPON_STATUS.UPCOMING, COUPON_STATUS.USED, COUPON_STATUS.EXPIRED].includes(
				status
			) && (
				<MyCouponInfo
					uses={uses}
					creationDate={creationDate}
					amount={amount}
					balance={balance}
					sourceBookingReference={sourceBookingReference}
					{...((isTitleLineClamped || isDescLineClamped) && {
						title: isCreditedFromFriendSponsor ? friendEmail : title,
						description: couponDescription,
						label: label,
					})}
					{...(status === COUPON_STATUS.USING && {
						remainingBalance: balance - usableAmount,
					})}
					status={status}
					isCreditedFromFriendSponsor={isCreditedFromFriendSponsor}
				/>
			)}
			{infoExt && infoExt}
		</div>
	);
};

MyCouponCredit.propTypes = {
	type: PropTypes.string.isRequired,
	title: messagePropTypeRequired,
	label: PropTypes.string,
	description: messagePropType,
	cta: PropTypes.element,
	icon: PropTypes.element,
	conditions: messagePropType,
	status: PropTypes.string,
	creationDate: PropTypes.number,
	minUseDate: PropTypes.number,
	maxUseDate: PropTypes.number,
	amount: PropTypes.number,
	usableAmount: PropTypes.number,
	balance: PropTypes.number,
	uses: PropTypes.arrayOf(
		PropTypes.shape({
			useDate: PropTypes.number,
			amount: PropTypes.number,
		})
	),
	friendEmail: PropTypes.string,
	sourceBookingReference: PropTypes.string,
	source: PropTypes.string,
	infoExt: PropTypes.element,
};

export default memo(MyCouponCredit);
